import MainHeader from '@/components/MainHeader';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Zap, Shield, Palette, Link2, Leaf, Sun } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import { ServiceSchema, BreadcrumbSchema, FAQPageSchema } from '@/components/SchemaOrg';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';

interface Product {
  id: string;
  name: string;
  slug: string;
  price: number;
  original_price: number | null;
  images: string[] | null;
  status: string;
  categories: string[] | null;
}

const StampaDtfUv = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, slug, price, original_price, images, status, categories')
        .contains('categories', ['inchiostri'])
        .eq('status', 'disponibile')
        .limit(4);

      if (!error && data) {
        setProducts(data);
      }
      setLoading(false);
    };

    fetchProducts();
  }, []);

  const handleAddToCart = (product: Product) => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.images?.[0] || '/placeholder.svg',
    });
    toast.success(`${product.name} aggiunto al carrello`);
  };

  const applications = [
    {
      icon: '🔩',
      title: 'Metallo',
      description: 'Stampa su alluminio, acciaio, rame con aderenza perfetta'
    },
    {
      icon: '🪟',
      title: 'Vetro & Ceramica',
      description: 'Decorazioni durature su vetro, specchi, piastrelle'
    },
    {
      icon: '🪵',
      title: 'Legno',
      description: 'Personalizzazione su legno naturale o trattato'
    },
    {
      icon: '💎',
      title: 'Plastica & Acrilico',
      description: 'Stampe brillanti su materiali sintetici'
    }
  ];

  const features = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Asciugatura Istantanea',
      description: 'La tecnologia UV garantisce una polimerizzazione immediata, aumentando la produttività.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Resistenza Superiore',
      description: 'Eccellente resistenza ai graffi, agli agenti atmosferici e ai raggi UV.'
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: 'Colori Brillanti',
      description: 'Gamut colore ampio e saturo, ideale per grafiche vivaci e di impatto.'
    },
    {
      icon: <Link2 className="w-8 h-8" />,
      title: 'Adesione Universale',
      description: 'Compatibile con un\'ampia gamma di materiali rigidi e flessibili.'
    },
    {
      icon: <Leaf className="w-8 h-8" />,
      title: 'Eco-Friendly',
      description: 'Formula a basso impatto ambientale, conforme alle normative europee.'
    },
    {
      icon: <Sun className="w-8 h-8" />,
      title: 'Uso Outdoor',
      description: 'Perfetto per applicazioni esterne grazie alla resistenza agli agenti atmosferici.'
    }
  ];

  const specs = [
    { label: 'Tipo di Inchiostro', value: 'UV DTF pigmentato' },
    { label: 'Colori Disponibili', value: 'Ciano, Magenta, Giallo, Nero, Bianco' },
    { label: 'Formato Confezione', value: '1000ml/bottiglia' },
    { label: 'Testine Compatibili', value: 'Epson i3200, XP600, DX5/DX7' },
    { label: 'Temperatura Utilizzo', value: '15-30°C' },
    { label: 'Certificazioni', value: 'REACH, RoHS, SVHC' }
  ];

  const faqs = [
    {
      question: "Cos'è l'inchiostro UV DTF?",
      answer: "L'inchiostro UV DTF è un tipo di inchiostro che si asciuga istantaneamente sotto luce UV, permettendo stampe su superfici rigide e flessibili con grande durabilità."
    },
    {
      question: "Su quali materiali posso stampare?",
      answer: "Puoi stampare su metallo, vetro, legno, ceramica, acrilico, plastica e molti altri materiali rigidi e semi-rigidi."
    },
    {
      question: "L'inchiostro UV DTF è resistente agli agenti atmosferici?",
      answer: "Sì, l'inchiostro UV DTF offre eccellente resistenza ai raggi UV, pioggia, umidità ed è ideale per applicazioni outdoor."
    },
    {
      question: "Quanto è durevole la stampa?",
      answer: "Le stampe realizzate con inchiostro UV DTF sono estremamente durature e resistono nel tempo agli agenti atmosferici, graffi e usura quotidiana."
    }
  ];

  return (
    <>
      <SEOHead
        title="Inchiostro DTF UV | Stampa UV DTF Professionale | Materialidtf.it"
        description="Inchiostro UV DTF di alta qualità con eccellente adesione su materiali rigidi e flessibili. Ideale per stampa su metallo, legno, vetro, acrilico e altri substrati."
        canonical="/stampa-dtf-uv"
        keywords="inchiostro UV DTF, stampa UV DTF, inchiostro UV, stampa metallo, stampa vetro, stampa legno"
      />
      <ServiceSchema
        name="Inchiostro DTF UV Premium"
        description="Inchiostro UV DTF professionale per stampa su materiali rigidi e flessibili. Asciugatura istantanea, colori brillanti e resistenza superiore."
        url="/stampa-dtf-uv"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Stampa DTF UV', url: '/stampa-dtf-uv' }
        ]}
      />
      <FAQPageSchema items={faqs} />

      <MainHeader />
      <Navigation />

      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary via-primary to-primary/80 py-16 px-4">
          <div className="container mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-6">
              Inchiostro DTF UV
            </h1>
            <p className="text-lg text-primary-foreground/90 max-w-3xl mx-auto">
              Inchiostro UV DTF di alta qualità con eccellente adesione su materiali rigidi e flessibili. Ideale per stampa su metallo, legno, vetro, acrilico e altri substrati non tessili.
            </p>
          </div>
        </section>

        {/* Product Spotlight */}
        <section className="py-16 px-4 bg-background">
          <div className="container mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <img 
                  src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/694415ba14157442c13a5ac2/06c77d036_image.png"
                  alt="Inchiostro DTF UV Premium - bottiglie per stampa su materiali rigidi" 
                  className="rounded-xl shadow-2xl w-full"
                  loading="lazy"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Inchiostro UV DTF Premium
                </h2>
                <p className="text-muted-foreground mb-8">
                  L'inchiostro UV DTF combina la versatilità della tecnologia DTF con l'istantanea polimerizzazione UV, consentendo stampe su superfici rigide e flessibili con estrema durabilità e resistenza agli agenti atmosferici.
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Tecnologia</p>
                    <p className="text-lg font-bold text-foreground">Polimerizzazione UV istantanea</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Applicazioni</p>
                    <p className="text-lg font-bold text-foreground">Metallo, Vetro, Legno, Acrilico</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Colori</p>
                    <p className="text-lg font-bold text-foreground">C M Y K WH</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Resistenza</p>
                    <p className="text-lg font-bold text-foreground">Graffi, UV, Acqua</p>
                  </div>
                </div>

                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  Richiedi un preventivo
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Applications Section */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-foreground text-center mb-4">
              Applicazioni Versatili
            </h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              L'inchiostro UV DTF apre infinite possibilità creative su materiali rigidi e flessibili
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {applications.map((app, index) => (
                <div key={index} className="bg-background rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-shadow">
                  <span className="text-4xl mb-4 block">{app.icon}</span>
                  <h3 className="text-xl font-bold text-foreground mb-2">{app.title}</h3>
                  <p className="text-muted-foreground text-sm">{app.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 px-4 bg-background">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Vantaggi dell'Inchiostro UV DTF
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-muted/30 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="text-primary mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-bold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Specifications */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Specifiche Tecniche
            </h2>
            <div className="bg-background rounded-xl shadow-lg overflow-hidden">
              {specs.map((spec, index) => (
                <div 
                  key={index} 
                  className={`flex justify-between items-center p-4 ${index !== specs.length - 1 ? 'border-b border-border' : ''}`}
                >
                  <span className="font-semibold text-foreground">{spec.label}</span>
                  <span className="text-muted-foreground">{spec.value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section className="py-16 px-4 bg-primary">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-primary-foreground text-center mb-12">
              I Nostri Prodotti UV DTF
            </h2>
            
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-80 rounded-xl" />
                ))}
              </div>
            ) : products.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {products.map((product) => (
                  <div key={product.id} className="bg-white rounded-xl overflow-hidden shadow-lg group">
                    <div className="relative aspect-square overflow-hidden">
                      {product.original_price && product.original_price > product.price && (
                        <span className="absolute top-2 left-2 bg-destructive text-white text-xs font-bold px-2 py-1 rounded z-10">
                          OFFERTA
                        </span>
                      )}
                      <Link to={`/prodotto/${product.slug}`}>
                        <img 
                          src={product.images?.[0] || '/placeholder.svg'}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </Link>
                    </div>
                    <div className="p-4">
                      <Link to={`/prodotto/${product.slug}`}>
                        <h3 className="font-bold text-foreground mb-2 hover:text-primary transition-colors">
                          {product.name}
                        </h3>
                      </Link>
                      <div className="flex items-center gap-2 mb-4">
                        {product.original_price && product.original_price > product.price && (
                          <span className="text-muted-foreground line-through text-sm">
                            €{product.original_price.toFixed(2)}
                          </span>
                        )}
                        <span className="text-lg font-bold text-primary">
                          €{product.price.toFixed(2)}
                        </span>
                      </div>
                      <Button 
                        className="w-full" 
                        onClick={() => handleAddToCart(product)}
                      >
                        Aggiungi al carrello
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-primary-foreground/80 text-center">Nessun prodotto disponibile al momento.</p>
            )}

            <div className="text-center mt-8">
              <Button asChild variant="secondary" size="lg">
                <Link to="/categoria/inchiostri">Vedi tutti i prodotti</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 bg-background">
          <div className="container mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Domande Frequenti
            </h2>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-muted/30 rounded-xl px-6 border-none"
                >
                  <AccordionTrigger className="text-foreground font-semibold hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-primary to-primary/80">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl font-bold text-primary-foreground mb-4">
              Pronto a Scoprire la Stampa UV DTF?
            </h2>
            <p className="text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
              Contattaci per una consulenza gratuita sui nostri inchiostri UV DTF e scopri le infinite possibilità di personalizzazione
            </p>
            <Button size="lg" variant="secondary" className="text-lg px-8">
              Richiedi una Consulenza Gratuita
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default StampaDtfUv;
